import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@heroui/react';
import { useCallback, useEffect, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import useConfirmDialog from './hooks/useConfirmDialog';
import { ConfirmDialogConfig } from './type/ConfirmDialog';
import { EnvironmentTag } from '../EnvironmentTag';

/** Beehive SideSheet = Radix modal; outside `role="dialog"` gets no pointer events. Portal here when open. */
function openRadixDialogHost(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const nodes = document.querySelectorAll<HTMLElement>(
    '[role="dialog"][data-state="open"]'
  );
  return nodes.length ? (nodes[nodes.length - 1] ?? null) : null;
}

export type ConfirmDialogProps = ConfirmDialogConfig & {
  open: boolean;
  onCancel: () => void;
};

const ConfirmDialog = ({
  open,
  onCancel,
  cancelText,
  confirmText,
  content,
  danger,
  onConfirm,
  title,
  withExplanation,
  explanationPlaceholder,
  explanationLabel,
  hideCancel,
  explanationShouldValidate,
  showEnvironmentTag,
  ...rest
}: ConfirmDialogProps) => {
  const [explanation, setExplanation] = useState<string>('');

  const { t } = useTranslation();

  const buttonColor = danger ? 'danger' : 'primary';

  /** Resolve during render so the first paint portals inside Radix (avoids one dead frame on body). */
  const radixPortalHost: Element | undefined =
    open && typeof document !== 'undefined'
      ? (openRadixDialogHost() ?? undefined)
      : undefined;

  useEffect(() => {
    if (!open) return undefined;
    const prevPointerEvents = document.body.style.pointerEvents;
    document.body.style.pointerEvents = 'auto';
    setExplanation('');
    return () => {
      document.body.style.pointerEvents = prevPointerEvents;
    };
  }, [open]);

  let isConfirmDisabled = false;
  if (withExplanation) {
    isConfirmDisabled = explanationShouldValidate
      ? explanationShouldValidate(explanation)
      : !explanation;
  }

  const handleConfirm = useCallback(() => {
    onConfirm(explanation);
    setExplanation('');
  }, [explanation, onConfirm]);

  const handleExplanationKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isConfirmDisabled && e.code === 'Enter') {
        handleConfirm();
      }
    },
    [handleConfirm, isConfirmDisabled]
  );

  return (
    <Modal
      isOpen={open}
      onClose={onCancel}
      data-testid={rest['data-testid']}
      hideCloseButton
      isDismissable={false}
      portalContainer={radixPortalHost}
      classNames={{
        backdrop: 'z-[9100]',
        wrapper: 'z-[9101]'
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className='flex flex-col gap-2'>
              {showEnvironmentTag && <EnvironmentTag />}
              <span className='break-words'>{title}</span>
            </ModalHeader>
            <ModalBody>
              {typeof content === 'string' ? (
                <p className='text-sm text-on-surface-variant'>{content}</p>
              ) : (
                content
              )}
              {withExplanation && (
                <Input
                  label={explanationLabel || t('label.explanation')}
                  placeholder={explanationPlaceholder}
                  value={explanation}
                  onValueChange={setExplanation}
                  onKeyDown={handleExplanationKeyDown}
                  data-testid='confirm-dialog-explanation-input'
                />
              )}
            </ModalBody>
            <ModalFooter>
              {!hideCancel && (
                <Button
                  variant='light'
                  color={buttonColor}
                  onPress={onCancel}
                  data-testid='confirm-dialog-cancel-button'
                >
                  {cancelText || t('action.cancel')}
                </Button>
              )}
              <Button
                color={buttonColor}
                isDisabled={isConfirmDisabled}
                onPress={handleConfirm}
                data-testid='confirm-dialog-confirm-button'
              >
                {confirmText || t('action.confirm')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

ConfirmDialog.useConfirmDialog = 'useConfirmDialog';

export default Object.assign(ConfirmDialog, {
  useConfirmDialog
});
